import { useAuth0 } from "@auth0/auth0-react";
import { PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import { Button, Headline, Small } from "@fronterahealth/frontera-ui-components";

import { BASECAMP_API_BASE, apiFetch } from "@api/rest/api-fetch";
import { useSendRioMessage } from "@api/rest/types-and-hooks";
import { useRioProgress } from "@hooks/useRioProgress";
import { RioMessageRole, RioProgressEventType, RioTab, type RioTabValue } from "@pages/Templates/constants";

import { RioAttachButton, type RioAttachment, RioAttachmentChips } from "./RioAttachmentBar";
import { RioAttachmentPreviewList } from "./RioAttachmentPreview";
import { RioEditsList } from "./RioEditsList";
import { type RioMessage, type RioMessageList, ToolNameEnum } from "./rioMessages";

// Chat: polls while a turn is in flight; resolves the reply landing condition.
// Fetches USER + ASSISTANT only — no edit payloads needed here.
const rioChatQueryKey = (templateId: number) => ["rio-chat-messages", templateId];

// Edits: no polling. Refreshes only when the WS fires model_finalized
// (Rio finished its turn) or when sendMutation succeeds and we know a
// turn started. Fetches SYSTEM rows with structured tool_payload.
const rioEditsQueryKey = (templateId: number) => ["rio-edit-messages", templateId];

interface RioChatDrawerProps {
  templateId: number;
  templateName: string;
}

interface PendingTurn {
  // Optimistic placeholder shown while the POST is in flight and while
  // we're polling for Rio's reply. Dropped on error, or superseded by
  // server messages once the reply arrives. message_id is client-generated
  // so React can key on it without colliding with workflow-issued ids.
  message_id: string;
  content: string;
}

/** Translate a Rio progress event into user-facing status text. */
function progressStatusText(
  event: {
    event: string;
    tool?: string;
  } | null,
): string {
  if (!event) return "Thinking…";
  switch (event.event) {
    case RioProgressEventType.TOOL_CALLED:
      return event.tool ? `${humanizeTool(event.tool)}…` : "Working…";
    case RioProgressEventType.TOOL_COMPLETED:
      return "Thinking…";
    case RioProgressEventType.MODEL_FINALIZED:
      return "Finalizing reply…";
    default:
      return "Thinking…";
  }
}

/** Map snake_case tool names to present-continuous status phrases.
 *
 * Every tool in agent_rio.py should have an entry here so the live
 * status display always shows a readable phrase instead of the raw
 * function name. When you add a new tool, add it here too.
 */
function humanizeTool(toolName: string): string {
  const map: Record<string, string> = {
    // Template mutations (apply_* — also tracked in the Edits panel)
    [ToolNameEnum.apply_add_section]: "Adding section",
    [ToolNameEnum.apply_update_section]: "Updating section",
    [ToolNameEnum.apply_remove_section]: "Removing section",
    [ToolNameEnum.apply_move_section]: "Moving section",
    [ToolNameEnum.apply_add_field]: "Adding field",
    [ToolNameEnum.apply_update_field]: "Updating field",
    [ToolNameEnum.apply_remove_field]: "Removing field",
    [ToolNameEnum.apply_move_field]: "Moving field",
    [ToolNameEnum.apply_update_template_description]: "Updating description",
    [ToolNameEnum.apply_add_treatment_plan_domain]: "Adding domain",
    [ToolNameEnum.apply_update_treatment_plan_domain]: "Updating domain",
    [ToolNameEnum.apply_remove_treatment_plan_domain]: "Removing domain",
    [ToolNameEnum.apply_add_goal_field]: "Adding goal field",
    [ToolNameEnum.apply_update_goal_field]: "Updating goal field",
    [ToolNameEnum.apply_remove_goal_field]: "Removing goal field",
    [ToolNameEnum.apply_add_target_field]: "Adding target field",
    [ToolNameEnum.apply_update_target_field]: "Updating target field",
    [ToolNameEnum.apply_remove_target_field]: "Removing target field",
    // Prompt-authoring apply_* (PROC-35 / PROC-93). Not in ToolNameEnum yet —
    // these tools were added to template Rio after the enum's last regen.
    // Rename here won't regress the enum-driven Edits panel because these
    // tools don't emit SYSTEM edit rows the Edits list renders.
    apply_duplicate_prompt: "Duplicating prompt",
    apply_bulk_duplicate_prompts: "Duplicating prompts",
    apply_construct_new_prompt_for_field: "Drafting a new prompt",
    apply_draft_new_prompt_for_field: "Drafting a new prompt",
    apply_start_prompt_test_run: "Starting a test run",
    get_prompt_test_run_result: "Fetching test result",
    apply_open_bulk_prompt_pr: "Opening bulk PR",
    // Prompt-EDIT tools (PROC-102) — added to Rio-Templates so users can
    // edit existing prompts from the template drawer.
    apply_edit_prompt_draft: "Editing prompt draft",
    apply_open_edit_prompt_pr: "Opening edit PR",
    apply_open_edit_prompts_pr: "Opening bulk-edit PR",
    get_prompt_snapshot: "Reading prompt",
    get_prompt_draft_overrides: "Reading draft",
    list_learner_test_profiles: "Loading test profiles",
    list_pending_prompt_drafts_for_org: "Checking pending drafts",
    // Read tools
    get_template_snapshot: "Reading template",
    fetch_template_details: "Reading template",
    get_global_memory: "Reading memory",
    get_org_memory: "Reading org memory",
    get_base_memory: "Reading base memory",
    search_prompts: "Searching prompts",
    search_templates: "Searching templates",
    // Memory writes
    record_org_preference: "Saving org preference",
    record_base_preference: "Saving base preference",
    record_global_preference: "Saving global preference",
  };
  return map[toolName] ?? toolName;
}

export const RioChatDrawer: FC<RioChatDrawerProps> = ({ templateId, templateName }) => {
  const { user } = useAuth0();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<PendingTurn | null>(null);
  const [attachments, setAttachments] = useState<RioAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<RioTabValue>(RioTab.CHAT);
  // When the user switches to Edits we mark the current count as seen.
  // While they're on Chat, any growth above this threshold gets a dot.
  const [lastSeenEditCount, setLastSeenEditCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const qc = useQueryClient();
  const enabled = Number.isFinite(templateId) && templateId > 0;

  // Chat query: polls at 2 s only while a user turn is in flight, stops
  // as soon as the reply row lands. Light payload — USER + ASSISTANT only.
  const chatQuery = useQuery<RioMessageList>({
    queryKey: rioChatQueryKey(templateId),
    queryFn: () => apiFetch<RioMessageList>(`${BASECAMP_API_BASE}/templates/${templateId}/rio/messages`),
    enabled,
    staleTime: 0,
    refetchInterval: (q) => {
      const messages = q.state.data?.messages ?? [];
      if (!pending) return false;
      return messages.some((m) => m.message_id === `reply-${pending.message_id}`) ? false : 2000;
    },
  });

  // Edits query: no polling. Refreshed by the WS model_finalized event
  // so new edit rows appear as soon as Rio's turn completes, not on a timer.
  const editsQuery = useQuery<RioMessageList>({
    queryKey: rioEditsQueryKey(templateId),
    queryFn: () =>
      apiFetch<RioMessageList>(`${BASECAMP_API_BASE}/templates/${templateId}/rio/messages?include_edit_log=true`),
    enabled,
    staleTime: 0,
    refetchInterval: false,
  });

  const sendMutation = useSendRioMessage({
    mutation: {
      onSuccess: () => {
        // Only the chat query needs to know a turn started — it starts
        // polling for the reply. Edits will refresh on model_finalized.
        void qc.invalidateQueries({ queryKey: rioChatQueryKey(templateId) });
      },
    },
  });

  const { latestEvent } = useRioProgress({
    enabled: Number.isFinite(templateId) && templateId > 0,
    templateId,
  });

  const handleClose = () => {
    setOpen(false);
    void qc.invalidateQueries({ queryKey: ["template", templateId] });
    void qc.invalidateQueries({ queryKey: ["templates"] });
  };

  // Refs so timer callbacks can read current values without stale closures.
  // Updated via effects — the ~1-frame lag is irrelevant for 5-minute timers.
  const pendingRef = useRef(pending);
  const awaitingReplyRef = useRef(false);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  const lastFinalizedRef = useRef<typeof latestEvent>(null);
  useEffect(() => {
    if (!latestEvent || latestEvent.event !== RioProgressEventType.MODEL_FINALIZED) return;
    if (lastFinalizedRef.current === latestEvent) return;
    lastFinalizedRef.current = latestEvent;
    void qc.invalidateQueries({ queryKey: ["template", templateId] });
    void qc.invalidateQueries({ queryKey: rioChatQueryKey(templateId) });
    void qc.invalidateQueries({ queryKey: rioEditsQueryKey(templateId) });
    // model_finalized is per-template, not per-turn, so we cannot safely start
    // a recovery timer here — another editor's completed turn would trigger it
    // against our still-in-flight pending turn.
  }, [latestEvent, qc, templateId]);

  // Backstop: if the reply never arrives within 5 minutes, unblock the composer.
  // Guards with refs so the timer only fires when the same turn is still genuinely
  // awaiting a reply — not when a later turn is in flight or the reply already landed.
  useEffect(() => {
    const turnId = pending?.message_id;
    if (!turnId) return;
    const timer = setTimeout(() => {
      if (pendingRef.current?.message_id === turnId && awaitingReplyRef.current) {
        setError("Rio's reply didn't arrive — please try again.");
        setPending(null);
      }
    }, 5 * 60_000);
    return () => clearTimeout(timer);
  }, [pending?.message_id]);

  const allMessages: RioMessage[] = useMemo(() => chatQuery.data?.messages ?? [], [chatQuery.data]);

  const awaitingReply = useMemo(() => {
    if (!pending) return false;
    // Backend contract: assistant reply id is "reply-<user-message-id>".
    return !allMessages.some((m) => m.message_id === `reply-${pending.message_id}`);
  }, [pending, allMessages]);
  useEffect(() => {
    awaitingReplyRef.current = awaitingReply;
  }, [awaitingReply]);

  // When awaitingReply transitions true → false the reply landed in the chat
  // poll. Invalidate the edits query here as a reliable fallback for when
  // the WS model_finalized event fires before the SYSTEM rows are committed
  // (race) or is missed entirely. At this point all tool records for the
  // turn are guaranteed to be in the DB.
  const prevAwaitingReplyRef = useRef(false);
  useEffect(() => {
    const was = prevAwaitingReplyRef.current;
    prevAwaitingReplyRef.current = awaitingReply;
    if (was && !awaitingReply) {
      void qc.invalidateQueries({ queryKey: rioEditsQueryKey(templateId) });
    }
  }, [awaitingReply, qc, templateId]);

  // Chat tab: USER + ASSISTANT only. We layer on the optimistic pending
  // bubble for the user's own in-flight turn.
  const chatMessages: Array<RioMessage & { _pending?: boolean }> = useMemo(() => {
    const visible = allMessages.filter((m) => m.role === RioMessageRole.USER || m.role === RioMessageRole.ASSISTANT);
    if (!pending) return visible;
    const userSaved = visible.some((m) => m.message_id === pending.message_id);
    // Keep the optimistic bubble until the user's own message id appears in the
    // server list. Don't key on hasReply — the reply can arrive before the user
    // row does (eventual consistency), which would drop the bubble too early.
    if (userSaved) return visible;
    return [
      ...visible,
      {
        role: RioMessageRole.USER,
        content: pending.content,
        user_name: user?.name ?? null,
        message_id: pending.message_id,
        _pending: true,
      },
    ];
  }, [allMessages, pending, user?.name]);

  // Edits tab: SYSTEM rows from the dedicated edits query. Older rows
  // without a parsed `edit` payload are filtered so the panel only shows
  // actionable items.
  const editMessages = useMemo(
    () => (editsQuery.data?.messages ?? []).filter((m) => m.role === RioMessageRole.SYSTEM && m.edit),
    [editsQuery.data],
  );

  // Each new edit message means Rio just committed a template change — refetch.
  // prevEditCountRef stays null until the first data load so existing edits
  // on mount don't trigger a spurious refetch.
  const prevEditCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (!editsQuery.data) return;
    if (prevEditCountRef.current === null) {
      prevEditCountRef.current = editMessages.length;
      return;
    }
    if (editMessages.length > prevEditCountRef.current) {
      prevEditCountRef.current = editMessages.length;
      void qc.invalidateQueries({ queryKey: ["template", templateId] });
    }
  }, [editMessages.length, editsQuery.data, qc, templateId]);

  useEffect(() => {
    if (open) {
      // Defer to next tick so the textarea exists in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (activeTab !== RioTab.CHAT) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeTab, chatMessages.length, pending]);

  const handleSend = () => {
    const content = draft.trim();
    // `sendMutation.isPending` is only true for the brief POST round-trip;
    // `awaitingReply` covers the full turn (POST → poll → reply landed).
    // `uploadingAttachment` guards the keyboard shortcut path — the Send
    // button is already disabled during upload, but ⌘/Ctrl + Enter isn't.
    if (!content || awaitingReply || uploadingAttachment) return;
    const localId = crypto.randomUUID();
    setError(null);
    setPending({ message_id: localId, content });
    setDraft("");
    sendMutation.mutate(
      {
        templateId,
        // Pass `localId` as the message_id so the workflow uses the
        // same id the client tracks `pending` against. Without this,
        // the server mints its own id and the optimistic placeholder
        // never matches the saved row (→ duplicate + stuck status).
        data: {
          content,
          display_name: user?.name,
          message_id: localId,
          ...(attachments.length ? { attachments } : {}),
        },
      },
      {
        onSuccess: (res) => {
          // customFetch resolves 4xx without throwing, so check explicitly.
          if (res.status >= 400) {
            setError("Failed to send message");
            setPending((p) => (p?.message_id === localId ? null : p));
          } else {
            setAttachments([]);
          }
          // 200 means the signal was accepted; the reply arrives via polling.
        },
        onError: (e) => {
          setError(e.detail || "Failed to send message");
          setPending((p) => (p?.message_id === localId ? null : p));
        },
      },
    );
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  // Greeting shows by default — only suppressed when we have POSITIVE
  // evidence of an existing chat (200 response with a non-empty message
  // list). Errors, loading, and empty logs all keep the bubble visible.
  // Once they open the drawer or dismiss the bubble, it stays gone for
  // the rest of the session.
  // Mark all existing edits as seen the first time data lands, and mark
  // them seen every time the user switches to the Edits tab. Anything
  // that arrives while on the Chat tab triggers the unread dot.
  const firstDataLoad = useRef(true);
  useEffect(() => {
    if (firstDataLoad.current && editsQuery.data) {
      firstDataLoad.current = false;

      setLastSeenEditCount(editMessages.length);
    }
  }, [editsQuery.data, editMessages.length]);
  useEffect(() => {
    if (activeTab === RioTab.EDITS) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastSeenEditCount(editMessages.length);
    }
  }, [activeTab, editMessages.length]);
  const unreadEditCount = Math.max(editMessages.length - lastSeenEditCount, 0);

  return (
    <>
      {/* Floating Rio FAB — Clippy-style. Sits above the TemplateSaveFooter
          (z-50 + bottom-24) and hides when the drawer is open. */}
      <button
        type="button"
        aria-label="Open Rio chat"
        onClick={() => setOpen(true)}
        className={`fixed bottom-12 right-8 z-50 flex h-24 w-24 items-center justify-center transition hover:scale-105 ${
          open ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <img src="/rio.svg" alt="" className="h-20 w-20" />
      </button>

      {/* Backdrop */}
      <div
        aria-hidden
        className={`fixed inset-0 z-40 bg-black/10 transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <aside
        aria-label="Rio chat"
        aria-hidden={!open}
        className={`fixed top-0 right-0 z-50 flex h-screen w-[420px] max-w-[90vw] flex-col border-l border-border-subtle bg-bg-surface shadow-xl transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <header className="flex items-start justify-between border-b border-border-subtle px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/rio.svg" alt="" className="h-8 w-8 shrink-0" />
            <div className="min-w-0">
              <Headline size="small">Rio</Headline>
              <Small className="text-text-secondary truncate">{templateName}</Small>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close Rio chat"
            className="rounded p-1 text-text-secondary hover:bg-bg-default"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        {/* Tabs — Chat is the default; Edits surfaces Rio's structured
            tool log (additions, renames, deletes) without cluttering chat.
            Full-width 50/50 split with center-aligned labels so the active
            indicator spans an equal half of the drawer. */}
        <div className="flex border-b border-border-subtle">
          <button
            type="button"
            onClick={() => setActiveTab(RioTab.CHAT)}
            className={`relative flex-1 py-2.5 text-center text-sm font-medium border-b-2 transition-colors ${
              activeTab === RioTab.CHAT
                ? "border-limestone-900 text-limestone-900"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            Chat
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(RioTab.EDITS)}
            className={`relative flex-1 py-2.5 text-center text-sm font-medium border-b-2 transition-colors ${
              activeTab === RioTab.EDITS
                ? "border-limestone-900 text-limestone-900"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            <span className="inline-flex items-center justify-center gap-1.5">
              Edits
              {editMessages.length > 0 && (
                <span className="rounded-full bg-bg-default px-1.5 py-0.5 text-xs text-text-secondary">
                  {editMessages.length}
                </span>
              )}
              {unreadEditCount > 0 && activeTab !== RioTab.EDITS && (
                <span
                  aria-label={`${unreadEditCount} new edit${unreadEditCount === 1 ? "" : "s"}`}
                  className="h-1.5 w-1.5 rounded-full bg-limestone-900"
                />
              )}
            </span>
          </button>
        </div>

        {/* Tab content. ``overflow-x-hidden`` on the scroll container +
            ``min-w-0`` on the ``<ul>`` inside a flex column below force
            long unwrappable strings (URLs, code) to wrap within the
            drawer width instead of triggering a horizontal scrollbar. */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
          {(chatQuery.isLoading || (activeTab === RioTab.EDITS && editsQuery.isLoading)) && (
            <Small className="text-text-secondary">Loading…</Small>
          )}

          {activeTab === RioTab.CHAT && (
            <>
              {!chatQuery.isLoading && chatMessages.length === 0 && (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <img src="/rio.svg" alt="" className="h-16 w-16 mb-3 opacity-70" />
                  <Small className="text-text-secondary max-w-[280px]">
                    Start a conversation with Rio about this template. Ask for changes, fixes, or clarifying feedback —
                    Rio can edit sections and fields directly.
                  </Small>
                </div>
              )}
              <ul className="flex flex-col gap-3 min-w-0">
                {chatMessages.map((m) => {
                  // Multi-user attribution: a USER message is "yours" when
                  // its `user_name` matches your Auth0 display name.
                  // Edge case: two users with identical display names
                  // would collide — acceptable tradeoff (see commit history
                  // for why we dropped the stable-id user_id field).
                  const isOwnMessage = m.role === RioMessageRole.USER && m.user_name === user?.name;
                  const alignRight = isOwnMessage;
                  const authorLabel = m.user_name;
                  return (
                    <li key={m.message_id} className={`flex ${alignRight ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[85%]">
                        {m.role === RioMessageRole.ASSISTANT && (
                          <Small className="text-text-secondary mb-0.5 block">Rio</Small>
                        )}
                        {m.role === RioMessageRole.USER && !isOwnMessage && authorLabel && (
                          <Small className="text-text-secondary mb-0.5 block">{authorLabel}</Small>
                        )}
                        <div
                          className={`rounded-lg px-3 py-2 text-sm shadow-sm ${
                            isOwnMessage
                              ? "bg-limestone-100 text-text-primary whitespace-pre-wrap"
                              : m.role === RioMessageRole.USER
                                ? "bg-bg-default text-text-primary whitespace-pre-wrap"
                                : "bg-rio-100 text-text-primary" // bg-rio-100: want a "rio" brand token in Mosaic
                          } ${m._pending ? "opacity-70" : ""}`}
                        >
                          {m.role === RioMessageRole.ASSISTANT ? (
                            // Rio replies can include markdown (bold, lists,
                            // code, links). User input is rendered as plain
                            // text — we don't want users' inputs interpreted
                            // as markup.
                            //
                            // ``components.a`` override: open external links
                            // (PR URLs, dashboards, docs Rio surfaces) in a
                            // new tab so the drawer + template session don't
                            // navigate away. ``noopener noreferrer`` blocks
                            // the classic ``window.opener`` phishing vector.
                            // Explicit color + underline because Prose's
                            // link styling assumes lighter backgrounds and
                            // reads flat in the chat bubbles.
                            // ``break-words`` on the prose wrapper +
                            // ``overflow-wrap:anywhere`` via the inline arbitrary
                            // class force long unwrappable tokens (URLs,
                            // hashes, long identifiers) to break mid-word
                            // instead of pushing the bubble past the drawer
                            // width — that's what triggered the horizontal
                            // scrollbar QA reported.
                            <div className="prose prose-sm max-w-none text-text-primary break-words [overflow-wrap:anywhere] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_pre]:overflow-x-auto [&_pre]:max-w-full">
                              <ReactMarkdown
                                components={{
                                  a: ({ href, children, ...rest }) => (
                                    <a
                                      {...rest}
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-rio-600 underline hover:text-rio-700 break-all"
                                    >
                                      {children}
                                    </a>
                                  ),
                                }}
                              >
                                {m.content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            m.content
                          )}
                          {m.role === RioMessageRole.USER && m.attachments && m.attachments.length > 0 && (
                            <RioAttachmentPreviewList attachments={m.attachments} />
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
                {awaitingReply && (
                  <li className="flex justify-start">
                    <div className="max-w-[85%]">
                      <Small className="text-text-secondary mb-0.5 block">Rio</Small>
                      <div className="rounded-lg bg-rio-100 px-3 py-2 text-sm text-text-secondary shadow-sm">
                        {progressStatusText(latestEvent)}
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </>
          )}

          {activeTab === RioTab.EDITS && !editsQuery.isLoading && <RioEditsList edits={editMessages} />}
        </div>

        {/* Error banner — no Mosaic error-bg token yet; using closest surface + error text */}
        {error && (
          <div className="border-t border-border-subtle bg-bg-surface px-5 py-2 text-sm text-text-error-primary">
            {error}
          </div>
        )}

        {/* Composer */}
        <div className="border-t border-border-subtle px-5 py-3">
          <RioAttachmentChips
            attachments={attachments}
            onRemove={(uuid) => setAttachments((prev) => prev.filter((a) => a.file_uuid !== uuid))}
            disabled={awaitingReply || uploadingAttachment}
          />
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={awaitingReply}
            placeholder="Ask Rio to add a field, remove a section, etc."
            rows={3}
            className="w-full resize-none rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-limestone-900 focus:outline-none disabled:opacity-60"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <RioAttachButton
              onChange={setAttachments}
              onUploadingChange={setUploadingAttachment}
              scope={{ kind: "template", templateId }}
              disabled={awaitingReply}
            />
            <Button
              text={
                <span className="inline-flex items-center gap-1.5">
                  Send
                  <PaperAirplaneIcon className="h-4 w-4" />
                </span>
              }
              appearance="primary"
              onClick={handleSend}
              disabled={!draft.trim() || awaitingReply || uploadingAttachment}
            />
          </div>
        </div>
      </aside>
    </>
  );
};
